import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NikLogo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Film, Star, Users, CreditCard, Ticket, Bell, Sparkles,
  Plus, Pencil, Trash2, LogOut, Loader2, X, Home,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const COLORS = ["#0066FF", "#38BDF8", "#FFB800", "#FF3B30", "#22C55E"];
const emptyContent = {
  title: "", type: "movie", description: "", poster: "", poster2: "", backdrop: "",
  languages: [], genres: [], year: 2025, rating: "U/A", match: 90, duration: "",
  video_url: "", trailer_url: "", tags: [], is_original: false, top10_rank: null,
  featured: false, seasons: null, cast: [],
};

const TABS = [
  { id: "analytics", label: "Analytics", icon: LayoutDashboard },
  { id: "content", label: "Content", icon: Film },
  { id: "originals", label: "Originals", icon: Star },
  { id: "users", label: "Users", icon: Users },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "notifications", label: "Notifications", icon: Bell },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--nik-blue)]";

function ContentForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...emptyContent, ...initial, top10_rank: initial?.top10_rank ?? "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const csv = (v) => (Array.isArray(v) ? v.join(", ") : v);
  const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    const payload = {
      ...f,
      languages: Array.isArray(f.languages) ? f.languages : toArr(f.languages),
      genres: Array.isArray(f.genres) ? f.genres : toArr(f.genres),
      tags: Array.isArray(f.tags) ? f.tags : toArr(f.tags),
      cast: Array.isArray(f.cast) ? f.cast : toArr(f.cast),
      year: parseInt(f.year) || 2025,
      match: parseInt(f.match) || 0,
      top10_rank: f.top10_rank === "" || f.top10_rank === null ? null : parseInt(f.top10_rank),
      seasons: f.seasons === "" || f.seasons === null ? null : parseInt(f.seasons),
    };
    try {
      if (initial?.id) await api.put(`/admin/content/${initial.id}`, payload);
      else await api.post("/admin/content", payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121824] border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl font-bold text-white">{initial?.id ? "Edit" : "Add"} Content</h3>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title"><input data-testid="cf-title" className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Type">
            <select className={inputCls} value={f.type} onChange={(e) => set("type", e.target.value)}>
              <option value="movie">Movie</option><option value="series">Series</option>
              <option value="kids">Kids</option><option value="live">Live</option>
            </select>
          </Field>
          <Field label="Poster URL"><input className={inputCls} value={f.poster} onChange={(e) => set("poster", e.target.value)} /></Field>
          <Field label="Poster 2 URL (optional)"><input data-testid="cf-poster2" className={inputCls} value={f.poster2} onChange={(e) => set("poster2", e.target.value)} /></Field>
          <Field label="Backdrop URL"><input className={inputCls} value={f.backdrop} onChange={(e) => set("backdrop", e.target.value)} /></Field>
          <Field label="Video URL"><input className={inputCls} value={f.video_url} onChange={(e) => set("video_url", e.target.value)} /></Field>
          <Field label="Trailer URL"><input className={inputCls} value={f.trailer_url} onChange={(e) => set("trailer_url", e.target.value)} /></Field>
          <Field label="Languages (comma)"><input className={inputCls} value={csv(f.languages)} onChange={(e) => set("languages", e.target.value)} /></Field>
          <Field label="Genres (comma)"><input className={inputCls} value={csv(f.genres)} onChange={(e) => set("genres", e.target.value)} /></Field>
          <Field label="Tags (trending, new_releases...)"><input className={inputCls} value={csv(f.tags)} onChange={(e) => set("tags", e.target.value)} /></Field>
          <Field label="Cast (comma)"><input className={inputCls} value={csv(f.cast)} onChange={(e) => set("cast", e.target.value)} /></Field>
          <Field label="Year"><input type="number" className={inputCls} value={f.year} onChange={(e) => set("year", e.target.value)} /></Field>
          <Field label="Rating"><input className={inputCls} value={f.rating} onChange={(e) => set("rating", e.target.value)} /></Field>
          <Field label="Match %"><input type="number" className={inputCls} value={f.match} onChange={(e) => set("match", e.target.value)} /></Field>
          <Field label="Duration"><input className={inputCls} value={f.duration} onChange={(e) => set("duration", e.target.value)} /></Field>
          <Field label="Top 10 Rank (blank=none)"><input type="number" className={inputCls} value={f.top10_rank ?? ""} onChange={(e) => set("top10_rank", e.target.value)} /></Field>
          <Field label="Seasons (blank=none)"><input type="number" className={inputCls} value={f.seasons ?? ""} onChange={(e) => set("seasons", e.target.value)} /></Field>
        </div>
        <Field label="Description"><textarea className={inputCls + " h-20"} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="flex gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={f.is_original} onChange={(e) => set("is_original", e.target.checked)} /> KalamPrime Original
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured (Hero)
          </label>
        </div>
        <button data-testid="cf-save" onClick={save} disabled={busy} className="mt-5 w-full bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [content, setContent] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10, description: "" });
  const [newNotif, setNewNotif] = useState({ title: "", message: "" });

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    loadAll();
  }, [user, loading]);

  const loadContent = () => api.get("/content").then((r) => setContent(r.data));
  const loadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  const loadAll = () => {
    loadContent();
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    loadCoupons().catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  const delContent = async (id) => {
    await api.delete(`/admin/content/${id}`);
    toast.success("Deleted");
    loadContent();
  };

  const savePlan = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, {
        name: p.name, price_monthly: parseInt(p.price_monthly), price_annual: parseInt(p.price_annual),
        quality: p.quality, screens: parseInt(p.screens), ads: p.ads, badge: p.badge || "",
        features: p.features, active: p.active,
      });
      toast.success("Plan updated");
      api.get("/plans").then((r) => setPlans(r.data));
    } catch { toast.error("Update failed"); }
  };

  const createCoupon = async () => {
    try {
      await api.post("/admin/coupons", { ...newCoupon, discount_percent: parseInt(newCoupon.discount_percent), active: true });
      toast.success("Coupon created");
      setNewCoupon({ code: "", discount_percent: 10, description: "" });
      loadCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createNotif = async () => {
    try {
      await api.post("/admin/notifications", newNotif);
      toast.success("Notification sent");
      setNewNotif({ title: "", message: "" });
    } catch { toast.error("Failed"); }
  };

  if (loading || !user || user.role !== "admin") {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]"><Loader2 className="animate-spin text-slate-400" /></div>;
  }

  const stat = (label, value) => (
    <div className="bg-[#121824] border border-white/10 rounded-xl p-5">
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="font-display text-3xl font-extrabold text-white mt-1">{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#0B0E14]" data-testid="nikplay-admin-dashboard">
      {/* Sidebar */}
      <aside className="w-16 md:w-60 shrink-0 border-r border-white/10 bg-[#0B0E14] flex flex-col">
        <div className="p-4 border-b border-white/10">
          <div className="hidden md:block"><NikLogo size={30} /></div>
          <div className="md:hidden flex justify-center"><NikLogo size={28} showText={false} /></div>
          <div className="hidden md:block text-[10px] tracking-[0.3em] text-[var(--nik-gold)] font-bold mt-2">ADMIN</div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-[var(--nik-blue)] text-white" : "text-slate-400 hover:bg-white/5"}`}>
              <t.icon size={18} /><span className="hidden md:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-2 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-white/5">
            <Home size={18} /><span className="hidden md:inline">Back to App</span>
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-white/5">
            <LogOut size={18} /><span className="hidden md:inline">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 overflow-y-auto max-h-screen">
        <div className="p-5 md:p-8">
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-white mb-6 capitalize">{tab}</h1>

          {tab === "analytics" && analytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stat("Total Users", analytics.total_users)}
                {stat("Total Titles", analytics.total_content)}
                {stat("Revenue", `₹${analytics.total_revenue}`)}
                {stat("Paid Orders", analytics.paid_orders)}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-[#121824] border border-white/10 rounded-xl p-5">
                  <div className="text-sm font-semibold text-white mb-4">Weekly Viewership</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={analytics.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                      <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} />
                      <Tooltip contentStyle={{ background: "#0B0E14", border: "1px solid #1E293B", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="views" stroke="#0066FF" strokeWidth={3} dot={{ fill: "#38BDF8" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-[#121824] border border-white/10 rounded-xl p-5">
                  <div className="text-sm font-semibold text-white mb-4">Subscribers by Plan</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={analytics.plan_counts} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                        {analytics.plan_counts.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0B0E14", border: "1px solid #1E293B", borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-[#121824] border border-white/10 rounded-xl p-5">
                <div className="text-sm font-semibold text-white mb-4">Content by Type</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={analytics.type_counts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#0B0E14", border: "1px solid #1E293B", borderRadius: 8 }} />
                    <Bar dataKey="value" fill="#0066FF" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {(tab === "content" || tab === "originals") && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-sm">{(tab === "originals" ? content.filter((c) => c.is_original) : content).length} titles</span>
                <button data-testid="admin-add-content" onClick={() => { setEditing(tab === "originals" ? { ...emptyContent, is_original: true } : null); setShowForm(true); }}
                  className="flex items-center gap-2 bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg">
                  <Plus size={16} /> Add Title
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(tab === "originals" ? content.filter((c) => c.is_original) : content).map((c) => (
                  <div key={c.id} className="flex gap-3 bg-[#121824] border border-white/10 rounded-xl p-3">
                    <img src={c.poster} alt="" className="w-14 h-20 object-cover rounded-md" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white text-sm truncate">{c.title}</div>
                      <div className="text-xs text-slate-400 capitalize">{c.type} • {c.year}</div>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {c.is_original && <span className="text-[9px] bg-[var(--nik-gold)] text-black px-1.5 rounded font-bold">ORIGINAL</span>}
                        {c.featured && <span className="text-[9px] bg-[var(--nik-blue)] text-white px-1.5 rounded font-bold">HERO</span>}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`edit-content-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }} className="text-slate-300 hover:text-white"><Pencil size={15} /></button>
                        <button data-testid={`del-content-${c.id}`} onClick={() => delContent(c.id)} className="text-slate-300 hover:text-[var(--nik-live)]"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "users" && (
            <div className="bg-[#121824] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-left">
                  <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Role</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5">
                      <td className="px-4 py-3 text-white">{u.name}</td>
                      <td className="px-4 py-3 text-slate-300">{u.email}</td>
                      <td className="px-4 py-3"><span className="text-[var(--nik-gold)] uppercase text-xs font-bold">{u.subscription_plan}</span></td>
                      <td className="px-4 py-3 text-slate-300 capitalize">{u.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "subscriptions" && (
            <div className="space-y-4">
              {plans.map((p, idx) => (
                <div key={p.id} className="bg-[#121824] border border-white/10 rounded-xl p-5">
                  <div className="font-display font-bold text-white mb-3">{p.name}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Monthly ₹"><input type="number" className={inputCls} value={p.price_monthly} onChange={(e) => setPlans((pl) => pl.map((x, i) => i === idx ? { ...x, price_monthly: e.target.value } : x))} /></Field>
                    <Field label="Annual ₹"><input type="number" className={inputCls} value={p.price_annual} onChange={(e) => setPlans((pl) => pl.map((x, i) => i === idx ? { ...x, price_annual: e.target.value } : x))} /></Field>
                    <Field label="Quality"><input className={inputCls} value={p.quality} onChange={(e) => setPlans((pl) => pl.map((x, i) => i === idx ? { ...x, quality: e.target.value } : x))} /></Field>
                    <Field label="Screens"><input type="number" className={inputCls} value={p.screens} onChange={(e) => setPlans((pl) => pl.map((x, i) => i === idx ? { ...x, screens: e.target.value } : x))} /></Field>
                    <Field label="Badge"><input className={inputCls} value={p.badge} onChange={(e) => setPlans((pl) => pl.map((x, i) => i === idx ? { ...x, badge: e.target.value } : x))} /></Field>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <button data-testid={`save-plan-${p.id}`} onClick={() => savePlan(p)} className="bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg">Save Plan</button>
                    <button data-testid={`delete-plan-${p.id}`} onClick={async () => {
                      if (!window.confirm(`Remove ${p.name}? This cannot be undone.`)) return;
                      await api.delete(`/admin/plans/${p.id}`);
                      toast.success("Plan removed");
                      api.get("/plans").then((r) => setPlans(r.data));
                    }} className="text-slate-400 hover:text-[var(--nik-live)] flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/5 transition-colors">
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "coupons" && (
            <div className="space-y-5">
              <div className="bg-[#121824] border border-white/10 rounded-xl p-5">
                <div className="text-sm font-semibold text-white mb-3">Create Coupon</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Code"><input data-testid="coupon-code" className={inputCls} value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })} /></Field>
                  <Field label="Discount %"><input type="number" className={inputCls} value={newCoupon.discount_percent} onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: e.target.value })} /></Field>
                  <Field label="Description"><input className={inputCls} value={newCoupon.description} onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })} /></Field>
                </div>
                <button data-testid="create-coupon-btn" onClick={createCoupon} className="mt-3 bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg">Create</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {coupons.map((c) => (
                  <div key={c.code} className="bg-[#121824] border border-white/10 rounded-xl p-4 flex justify-between items-center">
                    <div>
                      <div className="font-mono font-bold text-[var(--nik-gold)]">{c.code}</div>
                      <div className="text-xs text-slate-400">{c.discount_percent}% • {c.description}</div>
                    </div>
                    <button onClick={async () => { await api.delete(`/admin/coupons/${c.code}`); loadCoupons(); }} className="text-slate-400 hover:text-[var(--nik-live)]"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "notifications" && (
            <div className="bg-[#121824] border border-white/10 rounded-xl p-5 max-w-xl">
              <div className="text-sm font-semibold text-white mb-3">Push Notification</div>
              <Field label="Title"><input data-testid="notif-title" className={inputCls} value={newNotif.title} onChange={(e) => setNewNotif({ ...newNotif, title: e.target.value })} /></Field>
              <div className="mt-3"><Field label="Message"><textarea className={inputCls + " h-24"} value={newNotif.message} onChange={(e) => setNewNotif({ ...newNotif, message: e.target.value })} /></Field></div>
              <button data-testid="send-notif-btn" onClick={createNotif} className="mt-3 bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2"><Bell size={15} /> Send</button>
            </div>
          )}
        </div>
      </div>

      {showForm && <ContentForm initial={editing} onClose={() => setShowForm(false)} onSaved={loadContent} />}
    </div>
  );
}
